import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { qaApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { QaEvaluationsParams } from '../types/api.js';

/**
 * i18nの現在言語をAPI呼び出し用のlocale値（'ja' | 'en'）に変換するフック
 * @graph-business i18nの現在言語をAPI呼び出し用locale（'ja'|'en'）に変換する
 * @graph-connects none
 */
function useApiLocale(): 'ja' | 'en' {
  const { i18n } = useTranslation();
  const lang = i18n.language ?? 'ja';
  return lang.startsWith('ja') ? 'ja' : 'en';
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA評価一覧をTanStack Query経由で取得。四半期・ユーザーメールでフィルタした評価リストを返す
 * @graph-connects /qa/evaluations [calls] QA評価一覧取得API
 * @graph-connects qaApi.getEvaluations [calls] QA評価一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useQaEvaluations(params?: QaEvaluationsParams) {
  return useQuery({
    queryKey: queryKeys.qa.evaluations(params),
    queryFn: () => qaApi.getEvaluations(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA評価詳細をTanStack Query経由で取得。評価ID指定で個別の評価結果・スコア詳細を返す
 * @graph-connects /qa/evaluations/:id [calls] QA評価詳細取得API
 * @graph-connects qaApi.getEvaluationDetail [calls] QA評価詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useQaEvaluationDetail(id: string | undefined) {
  const locale = useApiLocale();
  const safeId = id ?? '';
  return useQuery({
    queryKey: [...queryKeys.qa.evaluationDetail(safeId), locale] as const,
    queryFn: () => qaApi.getEvaluationDetail(safeId, locale),
    enabled: !!id,
  });
}
